/**
 * SecureEmbed Energy Portal - Persona Context
 *
 * React context for managing the selected sub-role state.
 * Persists selection to localStorage so it survives page refreshes.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { SUB_ROLES } from '../constants/personas';

const STORAGE_KEY = 'energySelectedSubRole';

interface PersonaContextType {
  selectedSubRole: string | null;
  setSubRole: (key: string) => void;
  clearSubRole: () => void;
}

const PersonaContext = createContext<PersonaContextType | undefined>(undefined);

export const PersonaProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [selectedSubRole, setSelectedSubRole] = useState<string | null>(null);

  // Load saved selection on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && SUB_ROLES[stored]) {
      setSelectedSubRole(stored);
    }
  }, []);

  const setSubRole = useCallback((key: string) => {
    setSelectedSubRole(key);
    localStorage.setItem(STORAGE_KEY, key);
  }, []);

  const clearSubRole = useCallback(() => {
    setSelectedSubRole(null);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  return (
    <PersonaContext.Provider value={{ selectedSubRole, setSubRole, clearSubRole }}>
      {children}
    </PersonaContext.Provider>
  );
};

export const usePersona = (): PersonaContextType => {
  const context = useContext(PersonaContext);
  if (context === undefined) {
    throw new Error('usePersona must be used within a PersonaProvider');
  }
  return context;
};
